import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/system";
import { Button, Checkbox, MenuItem, TableBody, TableCell, TableRow } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import InstalledStockItemsTableToolbar from "../../shared/components/InstalledStockItemsTableToolbar";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  changeInstalledStockItemsQuery,
  getCategories,
  getInstalledStockItems,
  getManufacturers, installStockItems, unlinkStockItems
} from "../customerMachineSlice";
import {
  ChangeInstalledStockItem,
  InstalledStockItem,
  InstalledStockItemsQuery,
  InstalledStockItemsQueryParameter
} from "../../inventory/models/models";
import FullHeight from "../../../shared/components/FullHeight";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import ImageBox from "../../../shared/components/ImageBox";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import Table from "../../../shared/components/tables/Table";
import { CustomerMachineState } from "../models/models";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import InternalStockDialog from "./InternalStockDialog";
import ActionToast from "../../../shared/components/ActionToast";
import { setError, setSuccess } from "../customerMachinerySlice";
import { NotificationMessage } from "../../../shared/models/models";
import LimitedQuantityFormat from "../../../shared/components/formats/LimitedQuantityFormat";
import Loader from "../../../shared/components/Loader";
import LinkOffIcon from "../../../shared/assets/icons/LinkOffIcon";
import useTranslation from "../../../shared/hooks/useTranslation";

type PartsAndConsumablesProps = {
  machineId: string;
}

const PartsAndConsumables = ({ machineId }: PartsAndConsumablesProps) => {
  const {
    installedStockItemsQuery,
    categories,
    manufacturers,
    installedStockItems,
    installedStockItemsLoading,
    success,
    error
  } = useAppSelector<CustomerMachineState>((state: any) => state.customerMachine);

  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const init = async () => {
    const newQuery: InstalledStockItemsQuery = { ...installedStockItemsQuery, machineId: machineId }
    dispatch(changeInstalledStockItemsQuery(newQuery));
    await dispatch(getCategories())
    await dispatch(getManufacturers())
    await dispatch(getInstalledStockItems(newQuery));
  };

  useEffect(() => {
    init();
  }, [machineId]);

  const isInstalledStockItemsQuery = (query: InstalledStockItemsQuery) => {
    if (query) {
      return query.search || query.manufacturerId || query.categoryId;
    }
  };

  const [selectedItems, setSelectedItems] = useState<InstalledStockItem[]>([]);
  const [isInternalStockDialogOpen, setIsInternalStockDialogOpen] = useState(false);

  const handleAdd = () => {
    setIsInternalStockDialogOpen(true);
  }

  const handleChangeSelection = () => {
    if (!installedStockItems?.length) {
      return;
    }

    if (selectedItems.length === installedStockItems?.length) {
      setSelectedItems([]);
      return;
    } else {
      setSelectedItems(installedStockItems);
    }
  }

  const handleSelect = (installedStockItem: InstalledStockItem) => {
    let item = selectedItems.find(x => x.id === installedStockItem.id);
    if (item) {
      setSelectedItems(selectedItems.filter(x => x.id !== installedStockItem.id));
    } else {
      setSelectedItems([...selectedItems, installedStockItem]);
    }
  }

  const handleFindInMarketplace = (item: InstalledStockItem) => {
    if (item) {
      navigate(`/search?productNumber=${item.stockItem.product.number}`);
    }
  };

  const handleUnlinkItem = async (item: InstalledStockItem) => {
    await dispatch(unlinkStockItems({ machineId: machineId, model: [item.id] }))
    await dispatch(getInstalledStockItems(installedStockItemsQuery));
  }

  const handleUnlinkItems = async () => {
    if (!selectedItems.length) {
      return;
    }

    await dispatch(unlinkStockItems({ machineId: machineId, model: selectedItems.map(x => x.id) }))
    setSelectedItems([]);
    await dispatch(getInstalledStockItems(installedStockItemsQuery));
  }

  const handleQueryChange = (value: InstalledStockItemsQuery) => {
    dispatch(changeInstalledStockItemsQuery(value));
    dispatch(getInstalledStockItems(value));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = installedStockItemsQuery.sortBy !== parameter ? false : !installedStockItemsQuery.sortDesc;
    const newQuery = { ...installedStockItemsQuery, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeInstalledStockItemsQuery(newQuery));
    dispatch(getInstalledStockItems(newQuery));
  };

  const handleInstallStockItems = async (value: ChangeInstalledStockItem[]) => {
    if (!value.length) {
      return;
    }

    await dispatch(installStockItems({ machineId: machineId, model: value }));
    await dispatch(getInstalledStockItems(installedStockItemsQuery));
  }

  const columns: TableColumnDefinition[] = [
    { title: translate("product"), parameter: InstalledStockItemsQueryParameter.number },
    { title: translate("brandCategory"), parameter: InstalledStockItemsQueryParameter.manufacturer },
    { title: translate("installedInStock") },
    { title: undefined }
  ];

  return (
    <Box>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Box className="text-18-regular mb-2" sx={{ color: "#747881" }}>
        {translate("toolsPartsLinked")}
      </Box>

      <InstalledStockItemsTableToolbar
        query={installedStockItemsQuery}
        categories={categories}
        manufacturers={manufacturers}
        onChange={handleQueryChange}
      />

      {installedStockItemsLoading && (
        <Box sx={{ mt: "100px" }}>
          <FullHeight verticalOffset="medium">
            <Loader />
          </FullHeight>
        </Box>
      )}

      {!installedStockItemsLoading && installedStockItems && installedStockItems?.length < 1 && (
        <FullHeight>
          <Box sx={{ display: "flex", flexDirection: "column", mt: "100px", alignItems: "center" }}>
            <p className="text-16-medium m-2">{translate("noItemsAssociated")}</p>
            {isInstalledStockItemsQuery(installedStockItemsQuery) ?
              "" :
              <Button variant="contained" onClick={handleAdd} sx={{ minWidth: "220px" }}>
                {translate("addNewItem")}
              </Button>}
          </Box>
        </FullHeight>
      )}

      {!installedStockItemsLoading && !!installedStockItems?.length && (
        <FullHeight>
          <Box>
            <Table>
              <SortTableHead
                columns={columns}
                sortBy={installedStockItemsQuery.sortBy}
                sortDesc={installedStockItemsQuery.sortDesc}
                onSort={handleSort}
                checkboxSelection={true}
                isChecked={selectedItems.length === installedStockItems.length}
                onChangeCheckbox={handleChangeSelection}
              />

              <TableBody>
                {installedStockItems?.map((item) => (
                  <TableRow key={item.id}
                    selected={!!selectedItems.find(x => x.id === item.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        key={item?.id}
                        color="primary"
                        checked={!!selectedItems.find(x => x.id === item.id)}
                        onChange={(event) => {
                          handleSelect(item)
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box className="flex items-center">
                        <ImageBox size="small" src={item.stockItem.file?.content} />
                        <Box className="ms-3">
                          <Box className="text-14-medium" color={"#333333"}>
                            {item.stockItem.product?.number}
                          </Box>
                          <Box className="text-12-medium" lineHeight={"17px"}>
                            {item.stockItem.alternativeNumber}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Box className="text-14-medium uppercase" color={"#333333"}>
                        {item.stockItem.product?.manufacturer?.name}
                      </Box>
                      <Box className="text-14-regular" color={"#747881"}>
                        {item.stockItem.product?.category?.name}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <LimitedQuantityFormat value={item.quantity} limit={item.inStockQuantity} />
                    </TableCell>

                    <TableCell>
                      <TableActionButton stopPropagation={true}>
                        <MenuItem onClick={() => handleFindInMarketplace(item)}>
                          <SearchOutlinedIcon />
                          {translate("findInMarketplace")}
                        </MenuItem>

                        <MenuItem
                          onClick={() => confirm({
                            title: translate("unlinkProductTitle"),
                            text: translate("unlinkProductText", { name: !selectedItems.find(x => x.id === item.id) ? item.stockItem.product.number : selectedItems.length + ' ' + (selectedItems.length === 1 ? translate("product") : translate("products")) }),
                            confirmButton: translate("yesUnlink"),
                            closeButton: tCommon("common.no"),
                            severity: "error",
                            onConfirm: () => selectedItems.find(x => x.id === item.id) ? handleUnlinkItems() : handleUnlinkItem(item),
                          })}>
                          <LinkOffIcon />
                          {translate("unlinkFromMachine")}
                        </MenuItem>
                      </TableActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box className="my-3 flex justify-end">
              <Button variant="contained" onClick={handleAdd} sx={{ width: "220px" }}>
                {translate("addItem")}
              </Button>
            </Box>
          </Box>
        </FullHeight>
      )}

      <InternalStockDialog
        key={`internal-stock-dialog-${isInternalStockDialogOpen}`}
        machineId={machineId}
        open={isInternalStockDialogOpen}
        onClose={() => setIsInternalStockDialogOpen(false)}
        onConfirm={(value) => {
          handleInstallStockItems(value);
          setIsInternalStockDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default PartsAndConsumables;
